// A real patient's seizure beside the model fitted to it (the HUP runs, ba-dynamics-sim-v0). For
// every bipolar contact the run carries two envelopes: the patient's own recording
// (`contact_envelope_observed`, on the `observed` time base, in seconds from the clinically marked
// onset) and the model's, through its observation model (`contact_envelope_model`, on the
// `signal` base, model onset at `onset_s`). `observation.json` names each contact and the parcel
// it sits in; `model.json` records the model, the fit and the time mapping. This reads them,
// aligns both envelopes at the onset and groups the contacts by electrode.

import type { DynamicsSim } from './dynamics.ts';
import { quantile } from './overlay.ts';
import { axisFromTimeBase, frameTimes, type TimeAxis } from './time.ts';
import type { CommonHeader, DynamicsHeader } from './types.ts';

const OBSERVED = 'contact_envelope_observed';
const MODEL = 'contact_envelope_model';

/** Whether a dynamics run carries a patient's recording beside the model (the HUP fits). */
export function isPatientComparison(h: CommonHeader): boolean {
  const arrays = (h as DynamicsHeader).arrays;
  return h.format === 'ba-dynamics-sim-v0' && !!arrays?.[OBSERVED] && !!arrays[MODEL];
}

/** The patient's id from the recordings input ('sub-HUP185' → 'HUP185'), else null. */
export function patientOf(h: Pick<DynamicsHeader, 'inputs'>): string | null {
  const rec = (h.inputs ?? []).find((i) => i.role === 'recordings' && typeof i.subject === 'string');
  return rec ? String(rec.subject).replace(/^sub-/, '') : null;
}

/** The recordings' dataset ('OpenNeuro ds004100 v1.1.3' → 'OpenNeuro ds004100'), else null. */
export function datasetOf(h: Pick<DynamicsHeader, 'inputs'>): string | null {
  const rec = (h.inputs ?? []).find((i) => i.role === 'recordings');
  return rec?.name ? rec.name.replace(/\s+v\d+(\.\d+)*$/, '') : null;
}

/** The Methods line: model, patient, data. */
export function comparisonCaption(h: DynamicsHeader): string {
  const model = h.model?.name?.replace(/\s*\([^)]*\)\s*$/, '') ?? 'Model';
  const who = patientOf(h);
  return [who ? `${model} fitted to ${who}` : model, datasetOf(h)].filter(Boolean).join(' · ');
}

/**
 * The recording's modality as the index names the run ('HUP070 · ECoG patient comparison' → 'ECoG').
 * The header can't say it: both HUP runs list their channels under `seeg_bipolar`.
 */
export function modalityOf(label: string | undefined): string | null {
  return (label && /\b(SEEG|sEEG|ECoG|iEEG|EEG)\b/.exec(label)?.[1]) || null;
}

export interface Contact {
  /** Column in both envelope arrays. */
  index: number;
  /** As the header lists it: 'LA01-LA02', 'EEG LG 01-Ref-EEG LG 02-Ref'. */
  name: string;
  /** For a lane: 'LA01-LA02', 'LG01-LG02'. */
  label: string;
  /** The anode's electrode ('LA', 'LG') and contact number. */
  electrode: string;
  number: number | null;
  /** The model parcel it sits in, by source name ('Left_Amygdala'); '' when it's in none. */
  parcel: string;
  /** The bipolar pair's midpoint (the run's surface RAS, mm); null where observation.json has none. */
  position: [number, number, number] | null;
  /** In the clinically marked seizure onset zone; resected at surgery. */
  soz: boolean;
  resected: boolean;
}

export interface ContactGroup {
  id: string;
  /** 'LA', or a block of a grid: 'LG 1–8'. */
  label: string;
  /** Contact indices, in header order. */
  contacts: number[];
  /** The parcel most of its contacts sit in ('' when none does). */
  parcel: string;
}

/** One contact name: 'EEG LG 01-Ref' → LG, 1, 'LG01'; 'LA01' → LA, 1, 'LA01'. */
export function parseContact(raw: string): { electrode: string; number: number | null; short: string } {
  const s = raw
    .replace(/^EEG\s+/i, '')
    .replace(/-Ref$/i, '')
    .trim();
  const m = /^([A-Za-z]+'?)\s*(\d+)$/.exec(s);
  if (!m) return { electrode: s, number: null, short: s.replace(/\s+/g, '') };
  return { electrode: m[1]!, number: Number(m[2]), short: `${m[1]}${m[2]!.padStart(2, '0')}` };
}

/** A bipolar channel's anode and cathode from its name, where `observation.json` doesn't give them. */
export function splitBipolar(name: string): [string, string] {
  const ref = /^(.+?-Ref)-(.+)$/i.exec(name);
  if (ref) return [ref[1]!, ref[2]!];
  const m = /^(.+?\d'?)-(.+)$/.exec(name);
  return m ? [m[1]!, m[2]!] : [name, ''];
}

/** More bipolar channels than this on one electrode (a grid) and it splits into blocks. */
const MAX_GROUP = 16;
/** A grid block: this many contacts by number (an 8 × 8 grid's rows). */
const BLOCK = 8;

const mostCommon = (xs: string[]): string => {
  const n = new Map<string, number>();
  for (const x of xs) if (x) n.set(x, (n.get(x) ?? 0) + 1);
  let best = '';
  for (const [x, k] of n) if (k > (n.get(best) ?? 0)) best = x;
  return best;
};

/** Contacts grouped by electrode, in header order; a grid splits into blocks of eight contacts. */
export function contactGroups(contacts: Contact[]): ContactGroup[] {
  const byElectrode = new Map<string, Contact[]>();
  for (const c of contacts) byElectrode.set(c.electrode, [...(byElectrode.get(c.electrode) ?? []), c]);
  const out: ContactGroup[] = [];
  const group = (id: string, label: string, cs: Contact[]) => out.push({ id, label, contacts: cs.map((c) => c.index), parcel: mostCommon(cs.map((c) => c.parcel)) });
  for (const [e, cs] of byElectrode) {
    if (cs.length <= MAX_GROUP || cs.some((c) => c.number === null)) {
      group(e, e, cs);
      continue;
    }
    const blocks = new Map<number, Contact[]>();
    for (const c of cs) {
      const b = Math.max(0, Math.floor((c.number! - 1) / BLOCK));
      blocks.set(b, [...(blocks.get(b) ?? []), c]);
    }
    for (const [b, bc] of [...blocks].sort((x, y) => x[0] - y[0])) group(`${e}:${b + 1}`, `${e} ${b * BLOCK + 1}–${(b + 1) * BLOCK}`, bc);
  }
  return out;
}

interface ObservationJson {
  model?: string;
  gain?: string;
  /** Each bipolar channel: its contacts, the midpoint between them (the run's surface RAS, mm), the
   *  model parcel it sits in, and whether the clinicians marked it in the seizure onset zone or it
   *  was resected. */
  channels?: { name: string; anode?: string; cathode?: string; midpoint_mm?: number[]; parcel?: string; soz?: boolean; resected?: boolean }[];
  observed?: { seizure?: string; feature?: string; source?: string; timebase?: string };
}

export interface ModelJson {
  name?: string;
  equations?: string[];
  references?: string[];
  parameters?: { K?: number; x0?: number[]; [k: string]: unknown };
  inference?: { method?: string; n_seizures?: number; data_feature?: string; [k: string]: unknown };
  time_mapping?: { seconds_per_model_unit?: number; observed_median_seizure_s?: number; leading_parcel?: string; onset_s?: number; rule?: string };
  connectome?: { source?: string; doi?: string; normalisation?: string };
}

export interface Envelope {
  /** `[sample][contact]`. */
  data: Float32Array;
  axis: TimeAxis;
  units: string;
}

export interface PatientComparison {
  patient: string | null;
  contacts: Contact[];
  groups: ContactGroup[];
  /** The patient's recording; its time is seconds from the clinically marked onset. */
  observed: Envelope;
  /** The model at the same contacts; its time is model time, with its onset at `onset`. */
  model: Envelope;
  onset: number;
  /** What each envelope is, from `observation.json`. */
  observation: ObservationJson;
  /** Display scales: the recording's 99.5th percentile and the model's peak, over every contact. */
  scale: { observed: number; model: number };
}

/** Read a comparison run's envelopes and contacts. */
export async function openComparison(sim: DynamicsSim): Promise<PatientComparison> {
  const h = sim.header;
  const od = h.arrays?.[OBSERVED];
  const md = h.arrays?.[MODEL];
  if (!od || !md) throw new Error(`${h.id} has no contact envelopes`);
  const C = od.shape[1] ?? 0;
  if (md.shape[1] !== C) throw new Error(`${h.id}: ${C} observed contacts, ${md.shape[1]} modelled`);
  const [obs, mod, meta] = await Promise.all([sim.array(OBSERVED), sim.array(MODEL), sim.files.maybeJson<ObservationJson>('observation.json')]);
  const named = Object.values(h.channels ?? {}).find((v): v is string[] => Array.isArray(v) && v.length === C);
  const rows = meta?.channels?.length === C ? meta.channels : null;
  const contacts: Contact[] = Array.from({ length: C }, (_, i) => {
    const row = rows?.[i];
    const name = row?.name ?? named?.[i] ?? `contact ${i + 1}`;
    const [a, c] = row?.anode ? [row.anode, row.cathode ?? ''] : splitBipolar(name);
    const pa = parseContact(a);
    const pc = c ? parseContact(c) : null;
    const m = row?.midpoint_mm;
    const position = Array.isArray(m) && m.length === 3 && m.every((x) => typeof x === 'number' && Number.isFinite(x)) ? (m as [number, number, number]) : null;
    return { index: i, name, label: pc ? `${pa.short}-${pc.short}` : pa.short, electrode: pa.electrode, number: pa.number, parcel: row?.parcel ?? '', position, soz: row?.soz === true, resected: row?.resected === true };
  });
  const axisOf = (tb: string | undefined) => {
    const base = tb ? h.time_bases?.[tb] : undefined;
    if (!base) throw new Error(`${h.id}: no time base ${tb ?? '(none)'}`);
    return axisFromTimeBase(base);
  };
  const observed: Envelope = { data: obs as Float32Array, axis: axisOf(od.timebase ?? 'observed'), units: od.units ?? '' };
  const model: Envelope = { data: mod as Float32Array, axis: axisOf(md.timebase ?? 'signal'), units: md.units ?? '' };
  if (observed.axis.frames * C !== observed.data.length || model.axis.frames * C !== model.data.length) throw new Error(`${h.id}: envelope lengths don't match their time bases`);
  let peak = 0;
  for (const v of model.data) if (v > peak) peak = v;
  const hi = quantile(observed.data, 0.995);
  return {
    patient: patientOf(h),
    contacts,
    groups: contactGroups(contacts),
    observed,
    model,
    onset: typeof h.onset_s === 'number' ? h.onset_s : 0,
    observation: meta ?? {},
    scale: { observed: hi > 0 ? hi : 1, model: peak > 0 ? peak : 1 },
  };
}

/** One contact's envelope as a series. `shift` moves its time (the model's onset to 0); `scale` divides its values. */
export function contactSeries(e: Envelope, contact: number, shift = 0, scale = 1): { x: Float64Array; y: Float32Array; raw: Float32Array } {
  const C = e.data.length / e.axis.frames;
  const raw = new Float32Array(e.axis.frames);
  for (let i = 0; i < raw.length; i++) raw[i] = e.data[i * C + contact]!;
  const x = frameTimes(e.axis).map((t) => t - shift);
  return { x, y: raw.map((v) => v / scale), raw };
}

/** The group whose contacts reach the highest recorded envelope: where the recording shows the seizure most. */
export function liveliestGroup(c: PatientComparison): ContactGroup | undefined {
  const C = c.contacts.length;
  const peak = new Float32Array(C);
  const d = c.observed.data;
  for (let i = 0; i < d.length; i++) {
    const k = i % C;
    if (d[i]! > peak[k]!) peak[k] = d[i]!;
  }
  let best: ContactGroup | undefined;
  let top = -Infinity;
  for (const g of c.groups) {
    const v = Math.max(...g.contacts.map((k) => peak[k]!));
    if (v > top) {
      top = v;
      best = g;
    }
  }
  return best;
}
